import { useState, useRef, useCallback, useEffect } from 'react';
import Breadcrumb from '../../components/feature/Breadcrumb';
import ToolsDropdown from '../../components/feature/ToolsDropdown';
import LoginModal from '../home/components/LoginModal';
import { useFreeTrialGate } from '../../hooks/useFreeTrialGate';
import UpgradeTrialModal from '../../components/feature/UpgradeTrialModal';
import MathRenderer from '../../components/base/MathRenderer';
import LearningMode from './components/LearningMode';
import PlotChart from './components/PlotChart';
import FormulaReferencePanel from './components/FormulaReferencePanel';
import SymbolPicker from './components/SymbolPicker';
import CompareFunctionsPanel from './components/CompareFunctionsPanel';
import ExamMode from './components/ExamMode';
import NotebookPanel, { NotebookEntry, ExamMistake } from './components/NotebookPanel';
import StepExplainer from './components/StepExplainer';
import PracticeGenerator from './components/PracticeGenerator';
import KeyboardShortcutsPanel from './components/KeyboardShortcutsPanel';
import SpeedStatsPanel, { SolveRecord } from './components/SpeedStatsPanel';
import { computeLocally, ComputeResult, ResultPod, SubPod } from './mathEngine';
import HighlightedTextarea from './components/HighlightedTextarea';
import RandomChallengeModal from './components/RandomChallengeModal';
import FormulaBuilder from './components/FormulaBuilder';
import SessionSummary from './components/SessionSummary';
import PersonalRecordsPanel, { PersonalBestBanner, saveRecord } from './components/PersonalRecordsPanel';
import ConceptMap from './components/ConceptMap';
import type { PersonalRecord } from './components/PersonalRecordsPanel';

// ─── Streak Utilities ─────────────────────────────────────────────────────────

const STREAK_KEY = 'gm_streak_v1';

function getTodayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getYesterdayStr(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function loadStreak(): number {
  try {
    const raw = localStorage.getItem(STREAK_KEY);
    if (!raw) return 0;
    const data = JSON.parse(raw) as { count: number; lastDate: string };
    const today = getTodayStr();
    const yesterday = getYesterdayStr();
    return data.lastDate === today || data.lastDate === yesterday ? data.count : 0;
  } catch { return 0; }
}

function bumpStreak(): number {
  try {
    const raw = localStorage.getItem(STREAK_KEY);
    const today = getTodayStr();
    if (!raw) {
      localStorage.setItem(STREAK_KEY, JSON.stringify({ count: 1, lastDate: today }));
      return 1;
    }
    const data = JSON.parse(raw) as { count: number; lastDate: string };
    if (data.lastDate === today) return data.count;
    const newCount = data.lastDate === getYesterdayStr() ? data.count + 1 : 1;
    localStorage.setItem(STREAK_KEY, JSON.stringify({ count: newCount, lastDate: today }));
    return newCount;
  } catch { return 1; }
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface HistoryItem {
  id: string;
  query: string;
  timestamp: Date;
  result: ComputeResult;
}

// ─── Pod Card ─────────────────────────────────────────────────────────────────

function PodCard({ pod, index, onExplain, isDark }: {
  pod: ResultPod;
  index: number;
  onExplain?: (pod: ResultPod) => void;
  isDark?: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const isInput  = pod.title === 'Input interpretation' || pod.title === 'Input';
  const isResult = pod.title === 'Result' || pod.title === 'Results' || pod.title === 'Exact result' || pod.title === 'Derivative' || pod.title.includes('Integral Result') || pod.title === 'Solutions' || pod.title === 'Simplified Form' || pod.title === 'Factored Form' || pod.title === 'Expanded Form' || pod.title === 'Limit' || pod.title === 'Taylor Series (first 6 terms)';
  const isSteps  = pod.title.toLowerCase().includes('step') || pod.title === 'Coefficients' || pod.title === 'Summary';
  const isPlot   = pod.title.toLowerCase().includes('plot') || pod.title.toLowerCase().includes('graph');
  const isStats  = pod.title === 'Basic Statistics' || pod.title === 'Input Data' || pod.title === 'Sorted Data';

  const accentClass = isResult ? 'from-emerald-500 to-teal-600'
    : isSteps  ? 'from-violet-500 to-fuchsia-600'
    : isPlot   ? 'from-orange-500 to-amber-600'
    : isInput  ? 'from-cyan-500 to-sky-600'
    : isStats  ? 'from-sky-500 to-indigo-600'
    : 'from-slate-400 to-slate-500';

  const bgClass = isResult
    ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800/50'
    : isSteps
    ? 'bg-violet-50 border-violet-200 dark:bg-violet-900/20 dark:border-violet-800/50'
    : isPlot
    ? 'bg-gray-950 border-gray-800'
    : isInput
    ? 'bg-cyan-50 border-cyan-200 dark:bg-cyan-900/20 dark:border-cyan-800/50'
    : isStats
    ? 'bg-sky-50 border-sky-200 dark:bg-sky-900/20 dark:border-sky-800/50'
    : 'bg-white border-gray-200 dark:bg-slate-800 dark:border-slate-700';

  const iconName = isResult ? 'ri-check-double-line'
    : isSteps  ? 'ri-route-line'
    : isPlot   ? 'ri-line-chart-line'
    : isInput  ? 'ri-terminal-line'
    : isStats  ? 'ri-bar-chart-line'
    : 'ri-information-line';

  const canExplain = !isPlot && !isInput;
  const titleColor = isPlot ? 'text-slate-200' : isDark ? 'text-slate-100' : 'text-slate-800';
  const textColor  = isPlot ? 'text-slate-300' : isDark ? 'text-slate-200' : 'text-gray-800';

  return (
    <div className={`rounded-xl border overflow-hidden transition-all duration-200 ${bgClass}`}
      style={{ animationDelay: `${index * 60}ms` }}>
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-5 py-3 cursor-pointer hover:brightness-95 transition-all"
      >
        <div className="flex items-center gap-3">
          <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${accentClass} flex items-center justify-center shadow-sm`}>
            <i className={`text-white text-xs ${iconName}`}></i>
          </div>
          <span className={`font-semibold text-sm ${titleColor}`}>{pod.title}</span>
        </div>
        <div className="flex items-center gap-2">
          {canExplain && onExplain && (
            <button
              onClick={e => { e.stopPropagation(); onExplain(pod); }}
              title="Explain this step"
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold transition-colors cursor-pointer whitespace-nowrap ${isPlot ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-violet-100 hover:bg-violet-200 text-violet-700 dark:bg-violet-900/40 dark:hover:bg-violet-800/50 dark:text-violet-300'}`}
            >
              <i className="ri-eye-line text-xs"></i>
              Explain
            </button>
          )}
          <i className={`ri-arrow-${expanded ? 'up' : 'down'}-s-line text-lg ${isPlot ? 'text-slate-400' : 'text-slate-400 dark:text-slate-500'}`}></i>
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-5 space-y-3">
          {pod.subpods.map((sp: SubPod, si: number) => (
            <div key={si} className="space-y-2">
              {sp.plotExpression && (
                <PlotChart expression={sp.plotExpression} xMin={sp.plotXMin ?? -5} xMax={sp.plotXMax ?? 5} />
              )}
              {sp.plaintext && (
                <MathRenderer content={sp.plaintext} className={`text-sm ${textColor}`} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function GeniusMath() {
  // ── Core state ────────────────────────────────────────────────────────────
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ComputeResult | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [showShareToast, setShowShareToast] = useState(false);
  const [isSharedQuery, setIsSharedQuery] = useState(false);
  const [showLearningMode, setShowLearningMode] = useState(false);
  const [showFormulaPanel, setShowFormulaPanel] = useState(false);
  const [showComparePanel, setShowComparePanel] = useState(false);
  const [showExamMode, setShowExamMode] = useState(false);
  const [showNotebook, setShowNotebook] = useState(false);
  const [notebookEntries, setNotebookEntries] = useState<NotebookEntry[]>([]);
  const [saveToast, setSaveToast] = useState(false);
  const [streak, setStreak] = useState(0);
  const [showExplainer, setShowExplainer] = useState(false);
  const [explainerPod, setExplainerPod] = useState<ResultPod | null>(null);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [isDark, setIsDark] = useState(false);
  const [showPractice, setShowPractice] = useState(false);
  const [examMistakes, setExamMistakes] = useState<ExamMistake[]>([]);
  // ── New: keyboard / timer / speed stats ──────────────────────────────────
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showSpeedStats, setShowSpeedStats] = useState(false);
  const [showRandomChallenge, setShowRandomChallenge] = useState(false);
  const [showFormulaBuilder, setShowFormulaBuilder] = useState(false);
  // ── Session Summary ───────────────────────────────────────────────────────
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const sessionSolvesRef = useRef(0);
  // ── Personal Records ──────────────────────────────────────────────────────
  const [showPersonalRecords, setShowPersonalRecords] = useState(false);
  const [personalBestInfo, setPersonalBestInfo] = useState<{ topic: string; timeSec: number; prevBest: number | null } | null>(null);
  // ── Concept Map ───────────────────────────────────────────────────────────
  const [showConceptMap, setShowConceptMap] = useState(false);
  // Suppress unused import warning
  const _prTypeRef = useRef<PersonalRecord | null>(null); void _prTypeRef;
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [timerDisplaySec, setTimerDisplaySec] = useState(0);
  const [lastSolveTimeSec, setLastSolveTimeSec] = useState<number | null>(null);
  const [solveRecords, setSolveRecords] = useState<SolveRecord[]>([]);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const timerStartRef = useRef<number | null>(null);
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => { setStreak(loadStreak()); }, []);

  // ── Timer helpers ─────────────────────────────────────────────────────────
  const startDisplayTimer = useCallback(() => {
    timerStartRef.current = Date.now();
    setTimerDisplaySec(0);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    timerIntervalRef.current = setInterval(() => {
      if (timerStartRef.current !== null) {
        setTimerDisplaySec(Math.floor((Date.now() - timerStartRef.current) / 1000));
      }
    }, 200);
  }, []);

  const stopDisplayTimer = useCallback((): number => {
    if (timerIntervalRef.current) { clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; }
    const elapsed = timerStartRef.current !== null
      ? (Date.now() - timerStartRef.current) / 1000
      : 0;
    timerStartRef.current = null;
    return elapsed;
  }, []);

  // ── Global keyboard shortcuts ─────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      if (e.key === 'Escape') {
        // Close modals in priority order
        if (showShortcuts) { setShowShortcuts(false); return; }
        if (showSpeedStats) { setShowSpeedStats(false); return; }
        if (showPractice) { setShowPractice(false); return; }
        if (showExplainer) { setShowExplainer(false); return; }
        if (showHistory) { setShowHistory(false); return; }
        if (showFormulaPanel) { setShowFormulaPanel(false); return; }
        if (showFormulaBuilder) { setShowFormulaBuilder(false); return; }
        if (showNotebook) { setShowNotebook(false); return; }
        return;
      }

      if (!isTyping && e.key === '?') {
        e.preventDefault();
        setShowShortcuts(v => !v);
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'k':
            e.preventDefault();
            textareaRef.current?.focus();
            textareaRef.current?.select();
            break;
          case 'd':
            if (!isTyping) { e.preventDefault(); setIsDark(v => !v); }
            break;
          case 'n':
            if (!isTyping) { e.preventDefault(); setShowNotebook(v => !v); }
            break;
          case 'e':
            if (!isTyping) { e.preventDefault(); setShowExamMode(true); }
            break;
          case 'p':
            if (!isTyping) { e.preventDefault(); setShowPractice(true); }
            break;
          case 'l':
            if (!isTyping) { e.preventDefault(); setShowLearningMode(true); }
            break;
          case 'f':
            if (!isTyping) { e.preventDefault(); setShowFormulaPanel(true); }
            break;
          case 'b':
            if (!isTyping) { e.preventDefault(); setShowFormulaBuilder(true); }
            break;
          case 'h':
            if (!isTyping) { e.preventDefault(); setShowHistory(v => !v); }
            break;
          case 't':
            if (!isTyping) { e.preventDefault(); setTimerEnabled(v => !v); }
            break;
          case 'r':
            if (!isTyping) { e.preventDefault(); setShowSpeedStats(true); }
            break;
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showShortcuts, showSpeedStats, showPractice, showExplainer, showHistory, showFormulaPanel, showFormulaBuilder, showNotebook]);

  // ── Cleanup timer on unmount ──────────────────────────────────────────────
  useEffect(() => {
    return () => { if (timerIntervalRef.current) clearInterval(timerIntervalRef.current); };
  }, []);

  const categories = ['All', 'Algebra', 'Calculus', 'Statistics', 'Geometry', 'Physics', 'Chemistry'];

  const examplesByCategory: Record<string, string[]> = {
    All: ['Solve x^2 - 5x + 6 = 0', 'Integrate sin(x)*cos(x) dx', 'Plot y = x^3 - 3x', 'Derivative of ln(x^2 + 1)'],
    Algebra: ['Solve 3x^2 - 12x + 9 = 0', 'Factor x^3 - 8', 'Simplify (x^2 - 4)/(x - 2)', 'Expand (a + b)^5'],
    Calculus: ['Derivative of x^3 * sin(x)', 'Integrate e^x * cos(x) dx', 'Limit as x->0 of sin(x)/x', 'Taylor series of e^x at x=0'],
    Statistics: ['Mean of {3, 7, 2, 9, 4, 6}', 'Standard deviation of {1,2,3,4,5}', 'Normal distribution mean=0 sigma=1', 'Binomial distribution n=10 p=0.5'],
    Geometry: ['Area of a circle with radius 7', 'Volume of a sphere radius 5', 'Pythagorean theorem a=3 b=4', 'Surface area of a cylinder r=3 h=10'],
    Physics: ['F = ma, m=5kg a=9.8 m/s^2', 'Kinetic energy v=30 m/s m=2 kg', 'Ohm\'s law V=12V R=4 ohms', 'Speed of light in water'],
    Chemistry: ['Molar mass of H2SO4', 'Balance CH4 + O2 -> CO2 + H2O', 'pH of 0.01 M HCl solution', 'Boiling point of ethanol'],
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sharedQuery = params.get('q');
    if (sharedQuery) { setQuery(sharedQuery); setIsSharedQuery(true); handleSolve(sharedQuery); }
  }, []);

  const { exhausted: trialExhausted, consume: consumeTrial, remaining: trialRemaining, dots: trialDots, used: trialUsed } = useFreeTrialGate('genius-math');
  const [showUpgrade, setShowUpgrade] = useState(false);

  const handleSolve = useCallback(async (q?: string) => {
    const input = (q || query).trim();
    if (!input) return;
    // Free trial gate
    if (trialExhausted) {
      setShowUpgrade(true);
      return;
    }
    consumeTrial();
    setIsLoading(true);
    setResult(null);
    setIsSharedQuery(false);
    setLastSolveTimeSec(null);
    if (timerEnabled) startDisplayTimer();
    await new Promise(r => setTimeout(r, 80));
    const res = computeLocally(input);
    const elapsedSec = timerEnabled ? stopDisplayTimer() : 0;
    if (timerEnabled && elapsedSec > 0) {
      setLastSolveTimeSec(Number(elapsedSec.toFixed(1)));
      const record: SolveRecord = {
        id: Date.now().toString(),
        query: input,
        timeSec: Number(elapsedSec.toFixed(2)),
        success: res.success,
        podCount: res.pods.length,
        timestamp: new Date(),
      };
      setSolveRecords(prev => [...prev, record].slice(-100));
      // Personal records check
      const pb = saveRecord(input, Number(elapsedSec.toFixed(2)));
      if (pb.isPersonalBest) {
        setPersonalBestInfo({ topic: pb.topic, timeSec: Number(elapsedSec.toFixed(2)), prevBest: pb.prevBest });
      }
    }
    // Session summary trigger (every 10 solves)
    sessionSolvesRef.current += 1;
    if (sessionSolvesRef.current >= 10) {
      sessionSolvesRef.current = 0;
      setShowSessionSummary(true);
    }
    setResult(res);
    setIsLoading(false);
    if (res.success) {
      setHistory(prev => [{ id: Date.now().toString(), query: input, timestamp: new Date(), result: res }, ...prev.slice(0, 19)]);
      const newStreak = bumpStreak();
      setStreak(newStreak);
    }
  }, [query, timerEnabled, startDisplayTimer, stopDisplayTimer, trialExhausted, consumeTrial]);

  const handleShareResult = useCallback(() => {
    if (!query.trim()) return;
    const params = new URLSearchParams();
    params.set('q', query.trim());
    const shareUrl = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    navigator.clipboard.writeText(shareUrl).then(() => { setShowShareToast(true); setTimeout(() => setShowShareToast(false), 2000); });
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSolve();
  };

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = file.name;
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    if (['txt', 'md'].includes(ext)) {
      const reader = new FileReader();
      reader.onload = ev => {
        const text = (ev.target?.result as string).trim();
        setQuery(text.slice(0, 500));
        setUploadedFileName(name);
        setUploadedImage(null);
        textareaRef.current?.focus();
      };
      reader.readAsText(file);
    } else if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) {
      const reader = new FileReader();
      reader.onload = ev => { setUploadedImage(ev.target?.result as string); setUploadedFileName(name); };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  }, []);

  const visibleExamples = examplesByCategory[activeCategory] || examplesByCategory['All'];
  const displayPods = result?.pods?.filter(p => p.title !== 'Input interpretation' && p.title !== 'Input') || [];
  const inputPod = result?.pods?.find(p => p.title === 'Input interpretation' || p.title === 'Input');

  const handleSolveProblemFromLearning = useCallback((problemQuery: string) => {
    setQuery(problemQuery); setShowLearningMode(false); handleSolve(problemQuery);
  }, [handleSolve]);

  const insertAtCursor = useCallback((text: string) => {
    const textarea = textareaRef.current;
    if (!textarea) { setQuery(prev => prev + text); return; }
    const start = textarea.selectionStart ?? query.length;
    const end = textarea.selectionEnd ?? query.length;
    setQuery(query.slice(0, start) + text + query.slice(end));
    requestAnimationFrame(() => { textarea.focus(); const np = start + text.length; textarea.setSelectionRange(np, np); });
  }, [query]);

  const handleFormulaUse = useCallback((example: string) => { setQuery(example); handleSolve(example); }, [handleSolve]);

  const handleSaveToNotebook = useCallback(() => {
    if (!result?.success || !query.trim()) return;
    const entry: NotebookEntry = { id: Date.now().toString(), query: query.trim(), pods: result.pods, timestamp: new Date(), note: '', pinned: false };
    setNotebookEntries(prev => [entry, ...prev]);
    setSaveToast(true);
    setTimeout(() => setSaveToast(false), 2000);
  }, [query, result]);

  const handleUpdateNote = useCallback((id: string, note: string) => { setNotebookEntries(prev => prev.map(e => e.id === id ? { ...e, note } : e)); }, []);
  const handleDeleteEntry = useCallback((id: string) => { setNotebookEntries(prev => prev.filter(e => e.id !== id)); }, []);
  const handleTogglePin = useCallback((id: string) => { setNotebookEntries(prev => prev.map(e => e.id === id ? { ...e, pinned: !e.pinned } : e)); }, []);
  const handleClearNotebook = useCallback(() => { setNotebookEntries([]); }, []);
  const handleExplain = useCallback((pod: ResultPod) => { setExplainerPod(pod); setShowExplainer(true); }, []);

  const handleExamComplete = useCallback((wrongAnswers: { statement: string; userAnswer: string; correctAnswer: string; subject: string }[]) => {
    setExamMistakes(prev => [...wrongAnswers.map(w => ({ ...w })), ...prev].slice(0, 50));
  }, []);

  // ── Timer display string ──────────────────────────────────────────────────
  const timerStr = timerDisplaySec >= 60
    ? `${Math.floor(timerDisplaySec / 60)}m ${timerDisplaySec % 60}s`
    : `${timerDisplaySec}s`;

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'dark' : ''}`}>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50/30 dark:bg-slate-950 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900">

      {/* Header */}
      <header className="bg-white/80 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-700/60 sticky top-0 z-40 transition-colors duration-300">
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Breadcrumb />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center shadow-lg">
                  <i className="ri-calculator-line text-white text-xl"></i>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900 dark:text-white">Genius Math</h1>
                  <div className="flex items-center gap-2 mt-0.5">
                    <div className="flex items-center gap-1.5 px-2 py-0.5 bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800/50 rounded-full">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse"></div>
                      <span className="text-[10px] font-bold text-orange-700 dark:text-orange-300 tracking-wide">Wolfram Alpha Engine</span>
                    </div>
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-violet-50 dark:bg-violet-900/30 rounded-full">
                      <i className="ri-brain-line text-violet-600 dark:text-violet-400 text-[10px]"></i>
                      <span className="text-[10px] font-medium text-violet-700 dark:text-violet-300">Computational Intelligence</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Keyboard Shortcuts Button */}
              <button
                onClick={() => setShowShortcuts(true)}
                title="Keyboard shortcuts (?)"
                className="w-9 h-9 flex items-center justify-center rounded-xl border bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all cursor-pointer font-bold text-sm"
              >
                ?
              </button>

              {/* Dark Mode Toggle */}
              <button
                onClick={() => setIsDark(v => !v)}
                title={isDark ? 'Switch to light mode (Ctrl+D)' : 'Switch to dark mode (Ctrl+D)'}
                className={`w-9 h-9 flex items-center justify-center rounded-xl border transition-all cursor-pointer ${isDark ? 'bg-slate-700 border-slate-600 text-amber-300 hover:bg-slate-600' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}
              >
                <i className={isDark ? 'ri-sun-line' : 'ri-moon-line'}></i>
              </button>

              <button
                onClick={() => setShowComparePanel(true)}
                className="flex items-center gap-2 px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-git-compare-line"></i>
                Compare
              </button>
              <button
                onClick={() => setShowExamMode(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-900/30 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-timer-2-line"></i>
                Exam
              </button>
              <button
                onClick={() => setShowRandomChallenge(true)}
                title="Random Challenge — weighted toward your weakest heatmap cells"
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500 to-orange-500 hover:opacity-90 text-white rounded-lg text-sm font-semibold transition-all shadow-sm hover:shadow-md whitespace-nowrap cursor-pointer"
              >
                <i className="ri-dice-line"></i>
                Challenge
              </button>
              <button
                onClick={() => setShowPersonalRecords(true)}
                title="Personal Records"
                className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-800/40 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-700/50 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                <span>🏆</span>
                Records
              </button>
              <button
                onClick={() => setShowConceptMap(true)}
                title="Concept Map"
                className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mind-map text-sm"></i>
                Map
              </button>
              <button
                onClick={() => setShowNotebook(true)}
                className="relative flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line"></i>
                Notebook
                {notebookEntries.length > 0 && (
                  <span className="bg-violet-600 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center leading-none">
                    {notebookEntries.length > 9 ? '9+' : notebookEntries.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowLearningMode(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white rounded-lg text-sm font-semibold shadow-md hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-graduation-cap-line"></i>
                Learning
              </button>
              <button
                onClick={() => setShowHistory(v => !v)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-history-line"></i>
                History {history.length > 0 && <span className="bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300 text-xs px-1.5 py-0.5 rounded-full font-semibold">{history.length}</span>}
              </button>
              <ToolsDropdown isDarkMode={isDark} />
              <button
                onClick={() => setShowLogin(true)}
                className="text-sm font-semibold px-4 py-2 rounded-full border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all whitespace-nowrap cursor-pointer"
              >
                Sign In
              </button>
              {streak > 0 && (
                <div className="hidden md:flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg" title={`${streak}-day solving streak!`}>
                  <span className="text-lg leading-none">🔥</span>
                  <div className="flex flex-col leading-none">
                    <span className="text-white font-black text-sm">{streak}</span>
                    <span className="text-orange-100 text-[9px] font-semibold">streak</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {isSharedQuery && (
          <div className="mb-5 bg-gradient-to-r from-cyan-500 to-sky-500 rounded-xl p-4 text-white shadow-lg flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center"><i className="ri-share-forward-line text-xl"></i></div>
            <div>
              <div className="font-bold text-sm">Shared Query Loaded</div>
              <div className="text-cyan-100 text-xs">This problem was shared with you via link</div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-[420px_1fr] gap-8">

          {/* ── Left Panel ── */}
          <div className="space-y-5">

            {/* Wolfram Alpha Badge */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-5 text-white shadow-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <i className="ri-function-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="font-bold text-base">Wolfram Alpha Powered</div>
                  <div className="text-orange-100 text-xs">World's most powerful computational engine</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                {[{ label: 'Domains', value: '50+' }, { label: 'Algorithms', value: '10K+' }, { label: 'Data Sources', value: '1T+' }].map(({ label, value }) => (
                  <div key={label} className="bg-white/15 rounded-lg py-2">
                    <div className="font-bold text-lg leading-none">{value}</div>
                    <div className="text-orange-100 text-[10px] mt-0.5">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Input Card */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-6 shadow-sm transition-colors duration-300">
              <h2 className="text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <i className="ri-edit-line text-violet-500"></i>
                Enter Your Problem
                <div className="ml-auto flex items-center gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    title="Upload .txt or image file"
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-50 dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-attachment-2 text-xs"></i>
                    Attach
                  </button>
                  <button
                    onClick={() => setShowFormulaPanel(true)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-violet-50 dark:bg-violet-900/40 hover:bg-violet-100 dark:hover:bg-violet-800/50 text-violet-600 dark:text-violet-300 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-book-2-line text-xs"></i>
                    Formulas
                  </button>
                  <button
                    onClick={() => setShowFormulaBuilder(true)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-teal-50 dark:bg-teal-900/40 hover:bg-teal-100 dark:hover:bg-teal-800/50 text-teal-600 dark:text-teal-300 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                    title="Formula Builder (Ctrl+B)"
                  >
                    <i className="ri-function-add-line text-xs"></i>
                    Builder
                  </button>
                </div>
              </h2>

              <HighlightedTextarea
                ref={textareaRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Type any math, science, or computation problem…\ne.g. integrate x^2 sin(x) dx`}
                rows={5}
                isDark={isDark}
                wrapperClassName="mb-0"
              />

              {/* Image Preview */}
              {uploadedImage && (
                <div className="mt-3 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-600">
                  <div className="relative">
                    <img src={uploadedImage} alt={uploadedFileName ?? 'Uploaded'} className="w-full max-h-44 object-contain bg-slate-50 dark:bg-slate-800" />
                    <button onClick={() => { setUploadedImage(null); setUploadedFileName(null); }} className="absolute top-2 right-2 w-6 h-6 bg-slate-800/70 text-white rounded-full flex items-center justify-center hover:bg-red-500 transition-colors cursor-pointer">
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800/50 px-3 py-2 flex items-center gap-2">
                    <i className="ri-image-2-line text-amber-500 text-sm"></i>
                    <span className="text-xs text-amber-700 dark:text-amber-300 font-medium">{uploadedFileName}</span>
                    <span className="text-xs text-amber-600 dark:text-amber-400 ml-1">— type the problem from this image above</span>
                  </div>
                </div>
              )}

              {/* Text File Indicator */}
              {uploadedFileName && !uploadedImage && (
                <div className="mt-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 rounded-xl px-3 py-2.5 flex items-center gap-2">
                  <div className="w-6 h-6 bg-emerald-100 dark:bg-emerald-900/40 rounded-md flex items-center justify-center">
                    <i className="ri-file-text-line text-emerald-600 dark:text-emerald-400 text-xs"></i>
                  </div>
                  <span className="text-xs text-emerald-700 dark:text-emerald-300 font-medium flex-1 truncate">{uploadedFileName}</span>
                  <span className="text-[10px] text-emerald-600 font-bold bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 rounded-full">Loaded</span>
                  <button onClick={() => setUploadedFileName(null)} className="text-slate-400 hover:text-red-500 cursor-pointer ml-1"><i className="ri-close-line text-sm"></i></button>
                </div>
              )}

              <SymbolPicker onInsert={insertAtCursor} />

              {/* Bottom hint row with timer toggle */}
              <div className="flex items-center justify-between mt-3 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 dark:text-slate-500">Ctrl+Enter to solve</span>
                  <span className="text-slate-300 dark:text-slate-600">·</span>
                  {/* Timer Toggle */}
                  <button
                    onClick={() => setTimerEnabled(v => !v)}
                    title={timerEnabled ? 'Disable timer mode (Ctrl+T)' : 'Enable timer mode to track solve speed (Ctrl+T)'}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold transition-all cursor-pointer whitespace-nowrap ${
                      timerEnabled
                        ? 'bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 border border-sky-300 dark:border-sky-700'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 border border-transparent hover:border-slate-300'
                    }`}
                  >
                    <i className={`ri-timer-line text-xs ${timerEnabled ? 'text-sky-500 dark:text-sky-400' : ''}`}></i>
                    {timerEnabled ? 'Timer ON' : 'Timer'}
                  </button>
                  {/* Speed Stats button — only show if we have records */}
                  {solveRecords.length > 0 && (
                    <button
                      onClick={() => setShowSpeedStats(true)}
                      title="View speed stats"
                      className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400 border border-sky-200 dark:border-sky-800/50 hover:bg-sky-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-speed-line text-xs"></i>
                      Stats ({solveRecords.length})
                    </button>
                  )}
                </div>
                <span className="text-xs text-slate-400 dark:text-slate-500">{query.length} chars</span>
              </div>

              {/* trial dots */}
              <div className="flex items-center gap-2">
                {!trialExhausted ? (
                  <div className={`flex items-center gap-2 text-xs font-medium ${trialRemaining === 1 ? 'text-amber-600' : 'text-slate-500 dark:text-slate-400'}`}>
                    <div className="flex gap-1">
                      {trialDots.map((d, i) => (
                        <div key={i} className={`w-2 h-2 rounded-full ${d.active ? 'bg-gray-300 dark:bg-gray-600' : trialRemaining === 1 ? 'bg-amber-500' : 'bg-violet-400'}`} />
                      ))}
                    </div>
                    <span>{trialRemaining} free solve{trialRemaining !== 1 ? 's' : ''} left</span>
                  </div>
                ) : (
                  <button onClick={() => setShowUpgrade(true)} className="flex items-center gap-1.5 text-xs font-bold text-violet-600 dark:text-violet-400 hover:underline cursor-pointer whitespace-nowrap">
                    <i className="ri-sparkling-2-fill text-xs"></i>Upgrade to Solve More
                  </button>
                )}
              </div>

              <button
                onClick={() => handleSolve()}
                disabled={isLoading || !query.trim()}
                className={`w-full py-3.5 text-white font-bold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg disabled:cursor-not-allowed whitespace-nowrap ${
                  trialExhausted
                    ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:opacity-90'
                    : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 disabled:from-slate-300 disabled:to-slate-400 dark:disabled:from-slate-700 dark:disabled:to-slate-600'
                }`}
              >
                {isLoading ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Querying Wolfram Alpha…
                  {timerEnabled && <span className="ml-1 tabular-nums text-orange-100 font-bold">{timerStr}</span>}
                  </>
                ) : trialExhausted ? (
                  <><i className="ri-sparkling-2-fill text-lg"></i>Upgrade to Solve More</>
                ) : (
                  <><i className="ri-flashlight-line text-lg"></i>Solve with Wolfram Alpha</>
                )}
              </button>
            </div>

            {/* Category Filter */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-5 shadow-sm transition-colors duration-300">
              <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100 mb-3">Quick Examples</h3>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                      activeCategory === cat
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-sm'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div className="space-y-2">
                {visibleExamples.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => { setQuery(ex); handleSolve(ex); }}
                    className="w-full text-left px-3 py-2.5 bg-slate-50 dark:bg-slate-700/60 hover:bg-orange-50 dark:hover:bg-slate-700 hover:border-orange-200 dark:hover:border-slate-500 border border-slate-200 dark:border-slate-600 rounded-lg text-xs text-slate-700 dark:text-slate-200 font-mono transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-orange-400 mr-1"></i>{ex}
                  </button>
                ))}
              </div>
            </div>

            {/* Capabilities */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-5 shadow-sm transition-colors duration-300">
              <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100 mb-3">What Wolfram Alpha Can Solve</h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { icon: 'ri-function-line', label: 'Algebra & Calculus' },
                  { icon: 'ri-bar-chart-line', label: 'Statistics & Data' },
                  { icon: 'ri-compass-3-line', label: 'Geometry & Trig' },
                  { icon: 'ri-flask-line', label: 'Chemistry' },
                  { icon: 'ri-earth-line', label: 'Physics' },
                  { icon: 'ri-line-chart-line', label: 'Plotting & Graphs' },
                  { icon: 'ri-number-1', label: 'Number Theory' },
                  { icon: 'ri-translate-2', label: 'Unit Conversions' },
                ].map(({ icon, label }) => (
                  <div key={label} className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                    <div className="w-6 h-6 flex items-center justify-center"><i className={`${icon} text-orange-500 text-sm`}></i></div>
                    <span className="text-xs text-slate-700 dark:text-slate-300 font-medium">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Right Panel ── */}
          <div className="space-y-5">

            {/* Loading State */}
            {isLoading && (
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-orange-200 dark:border-orange-800/50 p-12 text-center shadow-sm">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg">
                  <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" style={{ borderWidth: 3 }}></div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Querying Wolfram Alpha</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">Running computational algorithms across 50+ knowledge domains…</p>
                {timerEnabled && (
                  <div className="mt-4 flex items-center justify-center gap-2 text-sky-600 dark:text-sky-400">
                    <i className="ri-timer-line text-base"></i>
                    <span className="font-black text-2xl tabular-nums">{timerStr}</span>
                  </div>
                )}
                <div className="flex justify-center gap-1.5 mt-5">
                  {[0, 1, 2, 3, 4].map(i => (
                    <div key={i} className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 120}ms` }}></div>
                  ))}
                </div>
              </div>
            )}

            {/* Error State */}
            {!isLoading && result && !result.success && (
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-red-200 dark:border-red-800/50 p-8 text-center shadow-sm">
                <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-error-warning-line text-red-500 text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">No Results Found</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">{result.error || 'Wolfram Alpha could not interpret this query.'}</p>
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 text-left">
                  <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Tips for better results:</p>
                  <ul className="text-xs text-slate-500 dark:text-slate-400 space-y-1">
                    <li>• Use <code className="bg-white dark:bg-slate-700 px-1 rounded">x^2</code> for x², <code className="bg-white dark:bg-slate-700 px-1 rounded">sqrt(x)</code> for √x</li>
                    <li>• Be specific: "solve x^2 - 4 = 0" not "solve equation"</li>
                    <li>• For calculus: "integrate sin(x) dx" or "derivative of cos(x)"</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Results */}
            {!isLoading && result?.success && (
              <>
                {/* Result Header */}
                <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-5 text-white shadow-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                        <i className="ri-check-double-line text-white text-xl"></i>
                      </div>
                      <div>
                        <div className="font-bold">Results from Wolfram Alpha</div>
                        {inputPod && <div className="text-orange-100 text-xs mt-0.5 font-mono">{inputPod.subpods?.[0]?.plaintext}</div>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {timerEnabled && lastSolveTimeSec !== null && (
                        <div className="text-right">
                          <div className="flex items-center gap-1.5 bg-white/20 rounded-lg px-3 py-1.5">
                            <i className="ri-timer-line text-orange-100"></i>
                            <span className="text-white font-black text-lg tabular-nums">
                              {lastSolveTimeSec >= 60
                                ? `${Math.floor(lastSolveTimeSec / 60)}m ${(lastSolveTimeSec % 60).toFixed(0)}s`
                                : `${lastSolveTimeSec.toFixed(1)}s`}
                            </span>
                          </div>
                          <div className="text-orange-100 text-[10px] text-right mt-0.5">solve time</div>
                        </div>
                      )}
                      <div className="text-right">
                        <div className="text-2xl font-bold">{displayPods.length}</div>
                        <div className="text-orange-100 text-xs">result pods</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pod Grid */}
                <div className="space-y-3">
                  {displayPods.map((pod, i) => (
                    <PodCard key={`${pod.title}-${i}`} pod={pod} index={i} onExplain={handleExplain} isDark={isDark} />
                  ))}
                </div>

                {/* Action Bar */}
                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-4 shadow-sm flex flex-wrap gap-3 transition-colors duration-300">
                  <button
                    onClick={() => { const text = result.pods.map(p => `${p.title}\n${p.subpods.map(s => s.plaintext).join('\n')}`).join('\n\n'); navigator.clipboard.writeText(text); }}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-file-copy-line"></i>Copy All
                  </button>
                  <button
                    onClick={handleSaveToNotebook}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white rounded-lg text-sm font-medium transition-all shadow-sm hover:shadow-md whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-book-open-line"></i>Notebook
                  </button>
                  <button
                    onClick={() => setShowPractice(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-lg text-sm font-medium transition-all shadow-sm hover:shadow-md whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-sparkling-2-line"></i>Practice
                  </button>
                  {timerEnabled && (
                    <button
                      onClick={() => setShowSpeedStats(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white rounded-lg text-sm font-medium transition-all shadow-sm hover:shadow-md whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-speed-line"></i>Speed Stats
                    </button>
                  )}
                  <button
                    onClick={handleShareResult}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-sky-500 hover:from-cyan-600 hover:to-sky-600 text-white rounded-lg text-sm font-medium transition-all shadow-sm hover:shadow-md whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-share-forward-line"></i>Share
                  </button>
                  <button
                    onClick={() => { setResult(null); setQuery(''); setLastSolveTimeSec(null); }}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-refresh-line"></i>New Problem
                  </button>
                  <a
                    href={`https://www.wolframalpha.com/input?i=${encodeURIComponent(query)}`}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800/50 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-external-link-line"></i>Wolfram Alpha
                  </a>
                </div>
              </>
            )}

            {/* Empty State */}
            {!isLoading && !result && (
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-14 text-center shadow-sm transition-colors duration-300">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <i className="ri-function-line text-4xl text-orange-500"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Wolfram Alpha Ready</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm max-w-sm mx-auto">
                  Enter any math, science, or computation problem. Press <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs font-mono">?</kbd> for keyboard shortcuts.
                </p>
                <div className="grid grid-cols-3 gap-3 mt-8 max-w-md mx-auto">
                  {[
                    { icon: 'ri-route-line', label: 'Step-by-Step', desc: 'Full working shown' },
                    { icon: 'ri-line-chart-line', label: 'Plots & Graphs', desc: 'Visual output' },
                    { icon: 'ri-timer-line', label: 'Timer Mode', desc: 'Track your speed' },
                  ].map(({ icon, label, desc }) => (
                    <div key={label} className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 text-center">
                      <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                        <i className={`${icon} text-orange-500`}></i>
                      </div>
                      <div className="text-xs font-bold text-slate-800 dark:text-slate-200">{label}</div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toasts */}
      {showShareToast && (
        <div className="fixed bottom-8 right-8 z-50 animate-[slideInUp_0.3s_ease-out]">
          <div className="bg-gradient-to-r from-cyan-500 to-sky-500 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center"><i className="ri-check-line text-xl"></i></div>
            <div>
              <div className="font-bold text-sm">Link Copied!</div>
              <div className="text-cyan-100 text-xs">Share this link with others</div>
            </div>
          </div>
        </div>
      )}

      {saveToast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 animate-[slideInUp_0.3s_ease-out]">
          <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-3">
            <i className="ri-book-open-line text-lg"></i>
            <span className="font-semibold text-sm">Saved to Notebook!</span>
            <button onClick={() => { setSaveToast(false); setShowNotebook(true); }} className="text-violet-200 text-xs underline cursor-pointer ml-2 whitespace-nowrap">View</button>
          </div>
        </div>
      )}

      {/* History Sidebar */}
      {showHistory && (
        <div className="fixed inset-y-0 right-0 w-80 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 shadow-2xl z-50 flex flex-col transition-colors duration-300">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
            <h3 className="font-bold text-slate-900 dark:text-white">Query History</h3>
            <button onClick={() => setShowHistory(false)} className="w-8 h-8 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg cursor-pointer">
              <i className="ri-close-line text-slate-500 dark:text-slate-400"></i>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {history.length === 0 ? (
              <div className="text-center py-12 text-slate-400 dark:text-slate-500 text-sm">No history yet</div>
            ) : history.map(item => (
              <button
                key={item.id}
                onClick={() => { setQuery(item.query); setResult(item.result); setShowHistory(false); }}
                className="w-full text-left p-3 bg-slate-50 dark:bg-slate-800 hover:bg-orange-50 dark:hover:bg-slate-700 hover:border-orange-200 border border-slate-200 dark:border-slate-700 rounded-xl transition-colors cursor-pointer"
              >
                <div className="text-xs font-mono text-slate-800 dark:text-slate-200 truncate">{item.query}</div>
                <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">{item.timestamp.toLocaleTimeString()} · {item.result.pods.length} pods</div>
              </button>
            ))}
          </div>
          {history.length > 0 && (
            <div className="p-4 border-t border-slate-200 dark:border-slate-700">
              <button onClick={() => setHistory([])} className="w-full py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors cursor-pointer">Clear History</button>
            </div>
          )}
        </div>
      )}

      {/* Hidden File Input */}
      <input ref={fileInputRef} type="file" accept=".txt,.md,.jpg,.jpeg,.png,.gif,.webp,.bmp" onChange={handleFileUpload} className="hidden" />

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      {showLearningMode && <LearningMode onSolveProblem={handleSolveProblemFromLearning} onClose={() => setShowLearningMode(false)} />}
      <FormulaReferencePanel show={showFormulaPanel} onClose={() => setShowFormulaPanel(false)} onUse={handleFormulaUse} />
      <FormulaBuilder
        show={showFormulaBuilder}
        onClose={() => setShowFormulaBuilder(false)}
        onInsert={(q) => { setQuery(q); setShowFormulaBuilder(false); }}
        onSolve={(q) => { setQuery(q); setShowFormulaBuilder(false); setTimeout(() => handleSolve(q), 50); }}
      />
      {showComparePanel && (
        <CompareFunctionsPanel
          onClose={() => setShowComparePanel(false)}
          onInsertExpr={(text) => { setQuery(text); handleSolve(text); setShowComparePanel(false); }}
        />
      )}
      {showExamMode && <ExamMode onClose={() => setShowExamMode(false)} onComplete={handleExamComplete} />}
      <NotebookPanel
        show={showNotebook}
        entries={notebookEntries}
        examMistakes={examMistakes}
        onClose={() => setShowNotebook(false)}
        onUpdateNote={handleUpdateNote}
        onDelete={handleDeleteEntry}
        onTogglePin={handleTogglePin}
        onClear={handleClearNotebook}
      />
      {showExplainer && explainerPod && (
        <StepExplainer pod={explainerPod} originalQuery={query} onClose={() => setShowExplainer(false)} />
      )}
      {showPractice && (
        <PracticeGenerator
          currentQuery={query}
          currentInterpretation={result?.inputInterpretation}
          onSolve={(q) => { setShowPractice(false); setQuery(q); handleSolve(q); }}
          onClose={() => setShowPractice(false)}
        />
      )}
      {showShortcuts && <KeyboardShortcutsPanel onClose={() => setShowShortcuts(false)} />}
      {showSpeedStats && (
        <SpeedStatsPanel
          records={solveRecords}
          onClose={() => setShowSpeedStats(false)}
          onClear={() => setSolveRecords([])}
        />
      )}
      {showRandomChallenge && (
        <RandomChallengeModal
          onSolve={(q) => { setShowRandomChallenge(false); setQuery(q); handleSolve(q); }}
          onClose={() => setShowRandomChallenge(false)}
        />
      )}
      <PersonalBestBanner info={personalBestInfo} onDismiss={() => setPersonalBestInfo(null)} />
      <SessionSummary
        show={showSessionSummary}
        onClose={() => setShowSessionSummary(false)}
        solveRecords={solveRecords}
        streak={streak}
      />
      <PersonalRecordsPanel
        show={showPersonalRecords}
        onClose={() => setShowPersonalRecords(false)}
        onSolve={(q) => { setQuery(q); handleSolve(q); }}
      />
      <ConceptMap
        show={showConceptMap}
        onClose={() => setShowConceptMap(false)}
        notebookEntries={notebookEntries}
        onSolve={(q) => { setQuery(q); handleSolve(q); }}
      />
      {showUpgrade && (
        <UpgradeTrialModal
          toolName="Genius Math"
          usedCount={trialUsed}
          onSignIn={() => { setShowUpgrade(false); setShowLogin(true); }}
          onClose={() => setShowUpgrade(false)}
        />
      )}
      </div>
    </div>
  );
}
